/**
 * The leaves of the template document (CLI-876 split it out of template.ts): the merge-field
 * catalogue and the content and colour checks every layer of the document reuses. Public names
 * are re-exported from `./template`; the schemas here are internal to the package.
 */
import { z } from 'zod'

/**
 * The merge fields a template may reference, with the sample each renders in a placeholder
 * preview. A catalogue rather than a bare enum so the editor's insert menu and the renderer's
 * resolution can never disagree about what exists. Deliberately small: the prototype's audit
 * flagged everything beyond these as needing data the pipeline doesn't reliably have yet.
 */
export const MERGE_FIELDS = [
  { token: 'client_name', label: 'Client name', sample: 'The Trade Desk' },
  { token: 'report_title', label: 'Report title', sample: 'Weekly Coverage Brief' },
  { token: 'date_range', label: 'Date range', sample: 'Jun 23 – Jun 29, 2026' },
  { token: 'send_date', label: 'Send date', sample: 'June 30, 2026' },
  // Legacy's `readable_date_string`: month and day, zero-padded — its single-header title, "Press
  // Clips: 10/22" (CLI-963).
  { token: 'send_date_short', label: 'Send date (MM/DD)', sample: '06/30' },
  { token: 'sender_name', label: 'Sender name', sample: 'Holly Newman' },
  // The email's subject line as it goes out, its date tokens rendered — the Rep. Zoe Lofgren
  // formatter's centred title over the coverage (CLI-973).
  {
    token: 'subject',
    label: 'Email subject',
    sample: 'Weekly Coverage Brief Press Clips: Jun 30, 2026',
  },
  // The address block portal templates put above everything else. Still understood, so templates
  // translated with it stay valid, but never printed and never offered (CLI-957): every recipient
  // saw every other's address, Bcc included.
  { token: 'recipients_to', label: 'To', sample: 'team@example.com', offered: false },
  { token: 'recipients_cc', label: 'Cc', sample: 'comms@example.com', offered: false },
  { token: 'recipients_bcc', label: 'Bcc', sample: '', offered: false },
] as const

/** The merge fields an author is offered — the editor's insert menu and the template prompt. */
export const OFFERED_MERGE_FIELDS = MERGE_FIELDS.filter(
  (field) => !('offered' in field) || field.offered,
)

export type MergeFieldToken = (typeof MERGE_FIELDS)[number]['token']

export function mergeFieldLabel(token: string): string {
  return MERGE_FIELDS.find((field) => field.token === token)?.label ?? token
}

/**
 * Rich-text block content. Bounded so a template row can't grow unreasonably, and refusing
 * `<script` outright: block HTML is written into the preview iframe and, eventually, into sent
 * email. The iframe is sandboxed and email clients don't execute script, so this is a guardrail
 * on top of those fences, not the sanitizer — a real allowlist sanitizer is a prerequisite for
 * the send path, which doesn't exist yet.
 */
/**
 * Constructs refused outright, with the reason each one is here:
 *
 * - `script`, `iframe`, `object`, `embed` — execute or embed something we did not author.
 * - `link`, `meta`, `base` — reach outside the document or rewrite where its URLs resolve.
 * - `on…=` handlers and `javascript:` URLs — script without a script tag, which is the form that
 *   actually survives a naive filter.
 *
 * This is a deny list, and a deny list is not a sanitizer: it stops the obvious rather than
 * guaranteeing the rest is safe. It got stricter when the editor gained a raw-HTML mode, because
 * "nobody can type that" stopped being true. A real allowlist sanitizer at render time is still
 * required before anything here is sent as email — see the note in report-render.
 */
export const FORBIDDEN_HTML: { pattern: RegExp; message: string }[] = [
  {
    pattern: /<\s*(script|iframe|object|embed|link|meta|base)\b/i,
    message: 'That tag is not allowed.',
  },
  { pattern: /\son[a-z]+\s*=/i, message: 'Event handler attributes are not allowed.' },
  { pattern: /javascript\s*:/i, message: 'javascript: URLs are not allowed.' },
]

export const richTextContentSchema = z
  .string()
  .max(20_000)
  .superRefine((html, ctx) => {
    for (const { pattern, message } of FORBIDDEN_HTML) {
      if (pattern.test(html)) ctx.addIssue({ code: 'custom', message })
    }
  })

export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb color')

/**
 * Clipbook's highlight yellow: the standard layout's principal colour and the canned choice in a
 * topic's highlight picker. Softer than legacy's #FBF719, which the analysts found harsh on screen
 * (Alex, 2026-10-22; Kaushik chose this shade 2026-10-23) — a deliberate departure from what
 * legacy sent, unlike the light blue, which is legacy's own.
 */
export const CLIPBOOK_HIGHLIGHT_YELLOW = '#fff59d'
